import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLanguage } from '../context/LanguageContext';
import { certificateAPI, settingsAPI, STORAGE_URL } from '../services/api';
import toast from 'react-hot-toast';
import CertificatePreview from '../components/certificates/CertificatePreview';
import ImageCropper from '../components/common/ImageCropper';
import { FiEye, FiFileText } from 'react-icons/fi';

// Translate names to Marathi using backend transliteration (better for proper nouns)
const transliterateName = async (text) => {
    if (!text || text.trim() === '') return '';

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/transliterate/to-marathi`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ text })
        });

        if (response.ok) {
            const data = await response.json();
            return data.transliterated || text;
        }
    } catch (error) {
        console.error('Backend transliteration error:', error);
    }

    return text;
};

// Translate addresses to Marathi using MyMemory API (better for sentences)
const translateAddress = async (text) => {
    if (!text || text.trim() === '') return '';

    try {
        // MyMemory API - Free tier: 1000 words/day
        const langpair = 'en|mr';
        const response = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${langpair}`);

        if (!response.ok) {
            throw new Error('Translation API failed');
        }

        const data = await response.json();

        if (data.responseStatus === 200 && data.responseData && data.responseData.translatedText) {
            const translated = data.responseData.translatedText;

            // Check if result contains Devanagari (Marathi script)
            const hasDevanagari = /[ऀ-ॿ]/.test(translated);

            if (hasDevanagari && translated !== text) {
                return translated;
            }

            // If no Devanagari found, try backend transliteration
            console.warn('MyMemory returned non-Marathi, trying backend...');
            return await transliterateName(text);
        }

        // Fallback to backend
        return await transliterateName(text);
    } catch (error) {
        console.error('Translation error:', error);
        // Fallback to backend transliteration
        return await transliterateName(text);
    }
};

const schema = yup.object().shape({
    husband_name: yup.string().required('वराचे नाव आवश्यक आहे / Husband name is required'),
    wife_name: yup.string().required('वधूचे नाव आवश्यक आहे / Wife name is required'),
    husband_age: yup.number().positive().integer().nullable().transform(value => (isNaN(value) ? null : value)),
    wife_age: yup.number().positive().integer().nullable().transform(value => (isNaN(value) ? null : value)),
});

const CreateCertificate = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [husbandPhoto, setHusbandPhoto] = useState(null);
    const [husbandSignature, setHusbandSignature] = useState(null);
    const [wifePhoto, setWifePhoto] = useState(null);
    const [wifeSignature, setWifeSignature] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [language, setLanguage] = useState('marathi'); // Default to Marathi

    // Image cropper state
    const [cropModal, setCropModal] = useState({
        isOpen: false,
        imageSrc: null,
        field: null,
        setFile: null,
        title: '',
    });
    const [settings, setSettings] = useState({
        stamp: null,
        logo: null,
        office_name: '',
        office_name_mr: '',
        office_address: '',
        office_address_mr: '',
        authority_name: '',
        authority_name_mr: '',
        place: '',
        place_mr: '',
        authority_signature: null
    });

    // Marathi transliteration state
    const [marathiFields, setMarathiFields] = useState({
        husband_name_mr: '',
        husband_address_mr: '',
        wife_name_mr: '',
        wife_address_mr: '',
    });

    // Loading state for translation
    const [isTranslating, setIsTranslating] = useState({
        husband_name: false,
        husband_address: false,
        wife_name: false,
        wife_address: false,
    });

    // Fetch settings (stamp, logo, office info) for preview + form defaults
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await settingsAPI.get();
                const s = {
                    stamp: response.data.stamp ? `${STORAGE_URL}/${response.data.stamp}` : null,
                    logo: response.data.logo ? `${STORAGE_URL}/${response.data.logo}` : null,
                    office_name: response.data.office_name || '',
                    office_name_mr: response.data.office_name_mr || '',
                    office_address: response.data.office_address || '',
                    office_address_mr: response.data.office_address_mr || '',
                    authority_name: response.data.authority_name || '',
                    authority_name_mr: response.data.authority_name_mr || '',
                    place: response.data.place || '',
                    place_mr: response.data.place_mr || '',
                    authority_signature: response.data.authority_signature ? `${STORAGE_URL}/${response.data.authority_signature}` : null,
                };
                setSettings(s);
                // Pre-fill form fields from settings
                reset({
                    husband_name: '',
                    wife_name: '',
                    office_name: s.office_name,
                    office_address: s.office_address,
                    authority_name: s.authority_name,
                    place: s.place,
                });
            } catch (err) {
                console.error('Failed to load settings for preview:', err);
            }
        };
        fetchSettings();
    }, []);

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            husband_name: '',
            wife_name: '',
            office_name: '',
            office_address: '',
            authority_name: '',
            place: '',
        }
    });

    // Watch all form values for live preview - use shallow comparison
    const formData = watch();

    // Debounced transliteration function using LibreTranslate
    const [transliterationTimeouts, setTransliterationTimeouts] = useState({});

    const transliterateField = useCallback(async (fieldName, englishValue) => {
        if (!englishValue || englishValue.trim() === '') {
            setMarathiFields(prev => ({
                ...prev,
                [`${fieldName}_mr`]: ''
            }));
            setIsTranslating(prev => ({
                ...prev,
                [fieldName]: false
            }));
            return;
        }

        // Clear previous timeout
        if (transliterationTimeouts[fieldName]) {
            clearTimeout(transliterationTimeouts[fieldName]);
        }

        // Show loading indicator
        setIsTranslating(prev => ({
            ...prev,
            [fieldName]: true
        }));

        // Set new timeout for debouncing
        const timeoutId = setTimeout(async () => {
            try {
                console.log(`🌐 Translating ${fieldName}:`, englishValue);

                // Use different APIs for names vs addresses
                let marathiText = '';

                if (fieldName === 'husband_name' || fieldName === 'wife_name') {
                    // Names: Use backend transliteration (better for proper nouns)
                    console.log('👤 Using backend transliteration for name...');
                    marathiText = await transliterateName(englishValue);
                } else {
                    // Addresses: Try MyMemory translation first, fallback to backend
                    console.log('📍 Using MyMemory translation for address...');
                    marathiText = await translateAddress(englishValue);
                }

                console.log(`✅ Translation result for ${fieldName}:`, marathiText);

                // Verify we got actual Devanagari text
                const hasDevanagari = /[ऀ-ॿ]/.test(marathiText);

                if (!hasDevanagari || marathiText === englishValue) {
                    console.warn('⚠️ No Devanagari found, trying backend as last resort...');
                    marathiText = await transliterateName(englishValue);
                }

                setMarathiFields(prev => ({
                    ...prev,
                    [`${fieldName}_mr`]: marathiText
                }));
            } catch (error) {
                console.error('Transliteration error:', error);
                // Fallback: keep English text
                setMarathiFields(prev => ({
                    ...prev,
                    [`${fieldName}_mr`]: englishValue
                }));
            } finally {
                setIsTranslating(prev => ({
                    ...prev,
                    [fieldName]: false
                }));
            }
        }, 500);

        setTransliterationTimeouts(prev => ({
            ...prev,
            [fieldName]: timeoutId
        }));
    }, [transliterationTimeouts]);

    // Watch English fields and transliterate
    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (name === 'husband_name') {
                transliterateField('husband_name', value.husband_name);
            } else if (name === 'husband_address') {
                transliterateField('husband_address', value.husband_address);
            } else if (name === 'wife_name') {
                transliterateField('wife_name', value.wife_name);
            } else if (name === 'wife_address') {
                transliterateField('wife_address', value.wife_address);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, transliterateField]);

    const onSubmit = async (data) => {
        setLoading(true);

        try {
            const formDataToSend = new FormData();

            Object.keys(data).forEach((key) => {
                if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
                    formDataToSend.append(key, data[key]);
                }
            });

            // Add Marathi fields
            formDataToSend.append('husband_name_mr', marathiFields.husband_name_mr);
            formDataToSend.append('husband_address_mr', marathiFields.husband_address_mr);
            formDataToSend.append('wife_name_mr', marathiFields.wife_name_mr);
            formDataToSend.append('wife_address_mr', marathiFields.wife_address_mr);

            // Add Marathi fields from settings (if available)
            if (settings.office_name_mr) {
                formDataToSend.append('office_name_mr', settings.office_name_mr);
            }
            if (settings.office_address_mr) {
                formDataToSend.append('office_address_mr', settings.office_address_mr);
            }
            if (settings.place_mr) {
                formDataToSend.append('place_mr', settings.place_mr);
            }
            if (settings.authority_name_mr) {
                formDataToSend.append('authority_name_mr', settings.authority_name_mr);
            }

            if (husbandPhoto) formDataToSend.append('husband_photo', husbandPhoto);
            if (husbandSignature) formDataToSend.append('husband_signature', husbandSignature);
            if (wifePhoto) formDataToSend.append('wife_photo', wifePhoto);
            if (wifeSignature) formDataToSend.append('wife_signature', wifeSignature);

            // Add language selection
            formDataToSend.append('language', language);

            await certificateAPI.create(formDataToSend);
            toast.success(t('certificate.createdSuccess'));
            navigate('/certificate-history');
        } catch (error) {
            console.error('Certificate creation error:', error);
            console.error('Error response:', error.response?.data);
            let message = t('certificate.createdSuccess').replace(t('certificate.createdSuccess'), 'Failed to create certificate');
            if (error.response?.data?.errors) {
                // Laravel validation errors
                const errors = error.response.data.errors;
                const errorMessages = Object.values(errors).flat();
                message = errorMessages.join(', ');
            } else if (error.response?.data?.message) {
                message = error.response.data.message;
            }
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e, setFile, fieldName) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error(t('certificate.fileSizeExceed'));
                return;
            }
            // Open cropper modal
            const reader = new FileReader();
            reader.onload = () => {
                setCropModal({
                    isOpen: true,
                    imageSrc: reader.result,
                    field: fieldName,
                    setFile: setFile,
                    title: fieldName.includes('Photo') ? 'Crop Photo' : 'Crop Signature',
                });
            };
            reader.readAsDataURL(file);
        }
        // Reset input so same file can be selected again
        e.target.value = '';
    };

    const handleCropComplete = (croppedFile) => {
        if (cropModal.setFile) {
            cropModal.setFile(croppedFile);
        }
        setCropModal({ isOpen: false, imageSrc: null, field: null, setFile: null, title: '' });
    };

    const handleCropCancel = () => {
        setCropModal({ isOpen: false, imageSrc: null, field: null, setFile: null, title: '' });
    };

    return (
        <div className="flex flex-col lg:flex-row gap-6 h-full">
            {/* Form Section */}
            <div className="flex-1 overflow-y-auto pb-24">
                <div className="bg-white shadow rounded-xl p-4 sm:p-6">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                        {t('certificate.newCertificate')}
                    </h1>
                    <p className="text-gray-500 mb-6">
                        {t('certificate.fillDetails')}
                    </p>

                    <form id="certificate-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                        {/* Language Selection Card */}
                        {/* <div className="bg-purple-50 rounded-xl p-4 sm:p-6 border border-purple-200">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">🌐</span>
                                {t('certificate.selectLanguage')}
                            </h2>
                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                                <label className={`flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-xl cursor-pointer transition-all ${
                                    language === 'marathi'
                                        ? 'bg-purple-600 text-white border-2 border-purple-600'
                                        : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-400'
                                }`}>
                                    <input
                                        type="radio"
                                        name="language"
                                        value="marathi"
                                        checked={language === 'marathi'}
                                        onChange={() => setLanguage('marathi')}
                                        className="sr-only"
                                    />
                                    <span className="text-xl sm:text-2xl">मराठी</span>
                                    <span className="text-xs sm:text-sm opacity-75">Marathi</span>
                                </label>
                                <label className={`flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-xl cursor-pointer transition-all ${
                                    language === 'english'
                                        ? 'bg-purple-600 text-white border-2 border-purple-600'
                                        : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-400'
                                }`}>
                                    <input
                                        type="radio"
                                        name="language"
                                        value="english"
                                        checked={language === 'english'}
                                        onChange={() => setLanguage('english')}
                                        className="sr-only"
                                    />
                                    <span className="text-xl sm:text-2xl">English</span>
                                    <span className="text-xs sm:text-sm opacity-75">इंग्रजी</span>
                                </label>
                            </div>
                            <p className="text-sm text-gray-500 mt-2">
                                {language === 'marathi'
                                    ? t('certificate.marathiWillGenerate')
                                    : t('certificate.englishWillGenerate')
                                }
                            </p>
                        </div> */}

                        {/* Registration Details Card */}
                        <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">1</span>
                                {t('certificate.registrationDetails')}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.serialNo')}
                                    </label>
                                    <input
                                        type="text"
                                        {...register('serial_no')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.volumeNo')}
                                    </label>
                                    <input
                                        type="text"
                                        {...register('volume_no')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.registrationDate')}
                                    </label>
                                    <input
                                        type="date"
                                        {...register('registration_date')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Husband Details Card */}
                        <div className="bg-blue-50 rounded-xl p-4 sm:p-6 border border-blue-200">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">2</span>
                                {t('certificate.groomDetails')}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="lg:col-span-2">
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.fullNameRequired')} (English)
                                    </label>
                                    <input
                                        type="text"
                                        {...register('husband_name')}
                                        className={`w-full px-4 py-3 text-lg border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.husband_name ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Enter full name in English"
                                    />
                                    {errors.husband_name && (
                                        <p className="text-red-500 text-sm mt-1">{errors.husband_name.message}</p>
                                    )}

                                    {/* Marathi Name Field */}
                                    <div className="mt-3">
                                        <label className="block text-base font-medium text-gray-700 mb-2">
                                            {t('certificate.fullNameRequired')} (मराठी / Marathi)
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={marathiFields.husband_name_mr}
                                                onChange={(e) => setMarathiFields(prev => ({
                                                    ...prev,
                                                    husband_name_mr: e.target.value
                                                }))}
                                                className={`w-full px-4 py-3 text-lg border ${isTranslating.husband_name ? 'border-yellow-400 bg-yellow-50' : 'border-orange-300 bg-orange-50'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
                                                placeholder="वराचे पूर्ण नाव मराठीत"
                                                disabled={isTranslating.husband_name}
                                            />
                                            {isTranslating.husband_name && (
                                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1 flex items-center">
                                            <span className={`${isTranslating.husband_name ? 'text-yellow-600' : 'text-green-600'}`}>
                                                {isTranslating.husband_name ? '⏳ Translating...' : '✓'}
                                            </span>
                                            <span className="ml-1">Auto-translated from English. You can edit if needed.</span>
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.age')}
                                    </label>
                                    <input
                                        type="number"
                                        {...register('husband_age')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="वर्षे"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.mobile')}
                                    </label>
                                    <input
                                        type="text"
                                        {...register('husband_mobile')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="+91..."
                                    />
                                </div>
                                <div className="lg:col-span-2">
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.address')} (English)
                                    </label>
                                    <textarea
                                        {...register('husband_address')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        rows={2}
                                        placeholder="Enter full address in English"
                                    />

                                    {/* Marathi Address Field */}
                                    <div className="mt-3">
                                        <label className="block text-base font-medium text-gray-700 mb-2">
                                            {t('certificate.address')} (मराठी / Marathi)
                                        </label>
                                        <div className="relative">
                                            <textarea
                                                value={marathiFields.husband_address_mr}
                                                onChange={(e) => setMarathiFields(prev => ({
                                                    ...prev,
                                                    husband_address_mr: e.target.value
                                                }))}
                                                className={`w-full px-4 py-3 text-lg border ${isTranslating.husband_address ? 'border-yellow-400 bg-yellow-50' : 'border-orange-300 bg-orange-50'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
                                                rows={2}
                                                placeholder="संपूर्ण पत्ता मराठीत"
                                                disabled={isTranslating.husband_address}
                                            />
                                            {isTranslating.husband_address && (
                                                <div className="absolute right-3 top-3">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1 flex items-center">
                                            <span className={`${isTranslating.husband_address ? 'text-yellow-600' : 'text-green-600'}`}>
                                                {isTranslating.husband_address ? '⏳ Translating...' : '✓'}
                                            </span>
                                            <span className="ml-1">Auto-translated from English. You can edit if needed.</span>
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.photo')}
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, setHusbandPhoto, 'husbandPhoto')}
                                        className="w-full text-base file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                                    />
                                    {husbandPhoto && (
                                        <img src={URL.createObjectURL(husbandPhoto)} alt="Preview" className="mt-2 w-20 h-20 object-cover rounded" />
                                    )}
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.signature')}
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, setHusbandSignature, 'husbandSignature')}
                                        className="w-full text-base file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                                    />
                                    {husbandSignature && (
                                        <img src={URL.createObjectURL(husbandSignature)} alt="Preview" className="mt-2 w-20 h-10 object-contain rounded border" />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Wife Details Card */}
                        <div className="bg-pink-50 rounded-xl p-4 sm:p-6 border border-pink-200">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <span className="w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">3</span>
                                {t('certificate.brideDetails')}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="lg:col-span-2">
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.fullNameRequired')} (English)
                                    </label>
                                    <input
                                        type="text"
                                        {...register('wife_name')}
                                        className={`w-full px-4 py-3 text-lg border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent ${
                                            errors.wife_name ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Enter full name in English"
                                    />
                                    {errors.wife_name && (
                                        <p className="text-red-500 text-sm mt-1">{errors.wife_name.message}</p>
                                    )}

                                    {/* Marathi Name Field */}
                                    <div className="mt-3">
                                        <label className="block text-base font-medium text-gray-700 mb-2">
                                            {t('certificate.fullNameRequired')} (मराठी / Marathi)
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={marathiFields.wife_name_mr}
                                                onChange={(e) => setMarathiFields(prev => ({
                                                    ...prev,
                                                    wife_name_mr: e.target.value
                                                }))}
                                                className={`w-full px-4 py-3 text-lg border ${isTranslating.wife_name ? 'border-yellow-400 bg-yellow-50' : 'border-pink-300 bg-pink-50'} rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent`}
                                                placeholder="वधूचे पूर्ण नाव मराठीत"
                                                disabled={isTranslating.wife_name}
                                            />
                                            {isTranslating.wife_name && (
                                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-600"></div>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1 flex items-center">
                                            <span className={`${isTranslating.wife_name ? 'text-yellow-600' : 'text-green-600'}`}>
                                                {isTranslating.wife_name ? '⏳ Translating...' : '✓'}
                                            </span>
                                            <span className="ml-1">Auto-translated from English. You can edit if needed.</span>
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.age')}
                                    </label>
                                    <input
                                        type="number"
                                        {...register('wife_age')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        placeholder="वर्षे"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.mobile')}
                                    </label>
                                    <input
                                        type="text"
                                        {...register('wife_mobile')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        placeholder="+91..."
                                    />
                                </div>
                                <div className="lg:col-span-2">
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.address')} (English)
                                    </label>
                                    <textarea
                                        {...register('wife_address')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        rows={2}
                                        placeholder="Enter full address in English"
                                    />

                                    {/* Marathi Address Field */}
                                    <div className="mt-3">
                                        <label className="block text-base font-medium text-gray-700 mb-2">
                                            {t('certificate.address')} (मराठी / Marathi)
                                        </label>
                                        <div className="relative">
                                            <textarea
                                                value={marathiFields.wife_address_mr}
                                                onChange={(e) => setMarathiFields(prev => ({
                                                    ...prev,
                                                    wife_address_mr: e.target.value
                                                }))}
                                                className={`w-full px-4 py-3 text-lg border ${isTranslating.wife_address ? 'border-yellow-400 bg-yellow-50' : 'border-pink-300 bg-pink-50'} rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent`}
                                                rows={2}
                                                placeholder="संपूर्ण पत्ता मराठीत"
                                                disabled={isTranslating.wife_address}
                                            />
                                            {isTranslating.wife_address && (
                                                <div className="absolute right-3 top-3">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-600"></div>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1 flex items-center">
                                            <span className={`${isTranslating.wife_address ? 'text-yellow-600' : 'text-green-600'}`}>
                                                {isTranslating.wife_address ? '⏳ Translating...' : '✓'}
                                            </span>
                                            <span className="ml-1">Auto-translated from English. You can edit if needed.</span>
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.photo')}
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, setWifePhoto, 'wifePhoto')}
                                        className="w-full text-base file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-pink-100 file:text-pink-700 hover:file:bg-pink-200"
                                    />
                                    {wifePhoto && (
                                        <img src={URL.createObjectURL(wifePhoto)} alt="Preview" className="mt-2 w-20 h-20 object-cover rounded" />
                                    )}
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.signature')}
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, setWifeSignature, 'wifeSignature')}
                                        className="w-full text-base file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-pink-100 file:text-pink-700 hover:file:bg-pink-200"
                                    />
                                    {wifeSignature && (
                                        <img src={URL.createObjectURL(wifeSignature)} alt="Preview" className="mt-2 w-20 h-10 object-contain rounded border" />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Marriage Details Card */}
                        <div className="bg-green-50 rounded-xl p-4 sm:p-6 border border-green-200">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">4</span>
                                {t('certificate.marriageDetails')}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.marriageDate')}
                                    </label>
                                    <input
                                        type="date"
                                        {...register('marriage_date')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.marriagePlace')}
                                    </label>
                                    <input
                                        type="text"
                                        {...register('marriage_place')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="मंदिर / सभामंडप"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Certificate Details Card */}
                        <div className="bg-yellow-50 rounded-xl p-4 sm:p-6 border border-yellow-200">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <span className="w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">5</span>
                                {t('certificate.certificateDetails')}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.officeName')}
                                        <span className="text-xs text-gray-400 ml-1">(from Settings)</span>
                                    </label>
                                    <input
                                        type="text"
                                        {...register('office_name')}
                                        readOnly
                                        className="w-full px-4 py-3 text-lg border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                                    />
                                </div>
                                <div className="lg:col-span-2">
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.officeAddress')}
                                        <span className="text-xs text-gray-400 ml-1">(from Settings)</span>
                                    </label>
                                    <textarea
                                        {...register('office_address')}
                                        readOnly
                                        className="w-full px-4 py-3 text-lg border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                                        rows={2}
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.place')}
                                        <span className="text-xs text-gray-400 ml-1">(from Settings)</span>
                                    </label>
                                    <input
                                        type="text"
                                        {...register('place')}
                                        readOnly
                                        className="w-full px-4 py-3 text-lg border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.certificateDate')}
                                    </label>
                                    <input
                                        type="date"
                                        {...register('certificate_date')}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-base font-medium text-gray-700 mb-2">
                                        {t('certificate.authorityName')}
                                        <span className="text-xs text-gray-400 ml-1">(from Settings)</span>
                                    </label>
                                    <input
                                        type="text"
                                        {...register('authority_name')}
                                        readOnly
                                        className="w-full px-4 py-3 text-lg border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                                        placeholder="नाव आणि पद"
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Preview Panel - Sticky on desktop */}
            <div className="hidden lg:block w-[420px] flex-shrink-0">
                <div className="sticky top-20">
                    <div className="bg-white shadow rounded-xl overflow-hidden">
                        <div className="bg-gray-100 px-4 py-3 border-b flex items-center justify-between">
                            <h3 className="font-semibold text-gray-800">{t('certificate.livePreview')}</h3>
                            <button
                                onClick={() => setShowPreview(true)}
                                className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                            >
                                <FiEye className="mr-1" /> {t('certificate.preview')}
                            </button>
                        </div>
                        <div className="p-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
                            <CertificatePreview
                                data={{ ...formData, office_name: formData.office_name || settings.office_name, office_address: formData.office_address || settings.office_address, authority_name: formData.authority_name || settings.authority_name, place: formData.place || settings.place }}
                                photos={{
                                    husbandPhoto,
                                    husbandSignature,
                                    wifePhoto,
                                    wifeSignature,
                                }}
                                language={language}
                                stamp={settings.stamp}
                                logo={settings.logo}
                                authoritySignature={settings.authority_signature}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Action Bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-30 lg:left-64">
                <div className="max-w-7xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                            <span className="hidden md:inline">{t('certificate.fillDetails')}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setShowPreview(true)}
                                className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                <FiEye className="mr-2" />
                                {t('certificate.preview')}
                            </button>
                            <button
                                type="submit"
                                form="certificate-form"
                                disabled={loading}
                                className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FiFileText className="mr-2" />
                                {loading ? t('certificate.generating') : t('certificate.generateCertificate')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Preview Modal */}
            {showPreview && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-auto">
                    <div className="bg-white rounded-xl w-full max-w-4xl max-h-[95vh] overflow-auto my-4">
                        <div className="sticky top-0 bg-gray-100 px-4 py-3 border-b flex items-center justify-between z-10">
                            <h3 className="font-semibold text-gray-800">{t('certificate.certificatePreview')}</h3>
                            <button
                                onClick={() => setShowPreview(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-4 flex justify-center">
                            <div className="w-full max-w-[794px]">
                                <CertificatePreview
                                    data={{ ...formData, office_name: formData.office_name || settings.office_name, office_address: formData.office_address || settings.office_address, authority_name: formData.authority_name || settings.authority_name, place: formData.place || settings.place }}
                                    photos={{
                                        husbandPhoto,
                                        husbandSignature,
                                        wifePhoto,
                                        wifeSignature,
                                    }}
                                    language={language}
                                    stamp={settings.stamp}
                                    logo={settings.logo}
                                    authoritySignature={settings.authority_signature}
                                />
                            </div>
                        </div>
                        <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex justify-end gap-3">
                            <button
                                onClick={() => setShowPreview(false)}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                            >
                                {t('certificate.close')}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowPreview(false);
                                    const form = document.getElementById('certificate-form');
                                    if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                                }}
                                disabled={loading}
                                className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                            >
                                <FiFileText className="mr-2" />
                                {loading ? t('certificate.generating') : t('certificate.generateCertificate')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Cropper Modal */}
            <ImageCropper
                isOpen={cropModal.isOpen}
                imageSrc={cropModal.imageSrc}
                onCropComplete={handleCropComplete}
                onCancel={handleCropCancel}
                title={cropModal.title}
            />
        </div>
    );
};

export default CreateCertificate;